import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { getAccessToken } from './AccountService';
import config from '../config.json';
import { ExploreResponse } from '../Models/FileShare';

const getHeaders = async (
  instance: IPublicClientApplication,
  account: AccountInfo
): Promise<Headers> => {
  const token = await getAccessToken(instance, account);
  const headers = new Headers();
  headers.append('Authorization', `Bearer ${token}`);
  headers.append('Content-Type', 'application/json');
  return headers;
};

export async function exploreFileShare(
  instance: IPublicClientApplication,
  account: AccountInfo
): Promise<ExploreResponse> {
  const headers = await getHeaders(instance, account);
  const response = await fetch(`${config.ptaasApi.baseUrl}/api/fileshare/explore`, {
    method: 'POST',
    headers,
  });
  if (!response.ok) throw new Error(`Explore failed: ${response.status}`);
  return response.json();
}

export async function getUploadUrl(
  instance: IPublicClientApplication,
  account: AccountInfo,
  section: 'users' | 'shared' | 'projects',
  filename: string,
  projectId?: string,
  folderPath?: string,
): Promise<string> {
  const headers = await getHeaders(instance, account);
  const response = await fetch(`${config.ptaasApi.baseUrl}/api/fileshare/upload-url`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ section, filename, projectId, folderPath }),
  });
  if (!response.ok) throw new Error(`Upload URL failed: ${response.status}`);
  const data = await response.json();
  return data.url;
}

export async function getDownloadUrl(
  instance: IPublicClientApplication,
  account: AccountInfo,
  blobPath: string
): Promise<string> {
  const headers = await getHeaders(instance, account);
  const response = await fetch(`${config.ptaasApi.baseUrl}/api/fileshare/download-url`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ blobPath }),
  });
  if (!response.ok) throw new Error(`Download URL failed: ${response.status}`);
  const data = await response.json();
  return data.url;
}

export async function deleteFile(
  instance: IPublicClientApplication,
  account: AccountInfo,
  path: string
): Promise<void> {
  const headers = await getHeaders(instance, account);
  const response = await fetch(`${config.ptaasApi.baseUrl}/api/fileshare/delete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ path }),
  });
  if (!response.ok) throw new Error(`Delete failed: ${response.status}`);
}

export async function createFolder(
  instance: IPublicClientApplication,
  account: AccountInfo,
  section: 'users' | 'shared' | 'projects',
  folderPath: string,
  projectId?: string,
): Promise<void> {
  const headers = await getHeaders(instance, account);
  const response = await fetch(`${config.ptaasApi.baseUrl}/api/fileshare/folders/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ section, folderPath, projectId }),
  });
  if (!response.ok) throw new Error(`Create folder failed: ${response.status}`);
}

export async function deleteFolder(
  instance: IPublicClientApplication,
  account: AccountInfo,
  section: 'users' | 'shared' | 'projects',
  folderPath: string,
  projectId?: string,
): Promise<void> {
  const headers = await getHeaders(instance, account);
  const response = await fetch(`${config.ptaasApi.baseUrl}/api/fileshare/folders/delete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ section, folderPath, projectId }),
  });
  if (!response.ok) throw new Error(`Delete folder failed: ${response.status}`);
}

export async function uploadFile(sasUrl: string, file: File): Promise<void> {
  const response = await fetch(sasUrl, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
}
